/**
 * Alerta "Projetos": alguma mensagem da conversa pede a atenção da equipe de
 * projetos e ainda não foi respondida. O Jev decide por mensagem; o alerta
 * some sozinho quando uma resposta resolve o assunto, ou quando alguém clica
 * "Tratado" (fica auditado). Abrir a conversa nunca apaga o alerta.
 *
 * - `SeloProjetos`: o sinal principal da linha na lista.
 * - `FaixaDoAlerta`: embaixo do cabeçalho da conversa, com o "Tratado".
 * - `useTirarSelo` + `DialogoTirarSelo`: "Não é de Projetos" numa mensagem
 *   (auditado; sem selo sobrando, o alerta fecha).
 * - `PendenteParaProjetos`: na prévia, o que foi pedido e ainda espera resposta.
 */
import React, { useCallback, useRef, useState } from "react";
import { FiCheck } from "react-icons/fi";
import styled from "styled-components";

import ConfirmDialog from "../../../components/ConfirmDialog";
import { useAuth } from "../../../context/AuthContext";
import { useToast } from "../../../context/ToastContext";
import { marcarAlertaTratado, tirarSeloProjetos } from "../../../services/centralProjetos";
import { alertaDaConversa, pendenteParaProjetos, textoDesde, textoDoAlerta } from "../apresentacao";
import { IconeProjetos } from "../Mensagens";
import { BotaoCompacto } from "./estilos";

const Selo = styled.span`
  display: inline-flex;
  align-items: center;
  gap: 4px;
  flex: 0 0 auto;
  height: 22px;
  padding: 0 8px 0 7px;
  border-radius: 11px;
  background: var(--warning-bg);
  color: var(--warning-text);
  box-shadow: inset 0 0 0 1px var(--warning-border);
  font-size: 12px;
  font-weight: 700;
  line-height: 1;
  white-space: nowrap;
  font-variant-numeric: tabular-nums;

  svg {
    flex: 0 0 auto;
  }
`;

export function SeloProjetos({ valor, rotulo }) {
  return (
    <Selo role="img" aria-label={rotulo} title={rotulo}>
      <IconeProjetos size={12} aria-hidden="true" />
      Projetos
      {valor ? <span aria-hidden="true">{valor}</span> : null}
    </Selo>
  );
}

const Faixa = styled.section.attrs({ className: "cp-faixa-alerta" })`
  display: flex;
  align-items: center;
  gap: 10px;
  min-width: 0;
  min-height: 46px;
  padding: 6px 12px 6px 16px;
  border-bottom: 1px solid var(--border);
  background: var(--warning-row);

  > svg {
    flex: 0 0 auto;
    color: var(--warning-text);
  }
`;

const TextoDaFaixa = styled.p`
  flex: 1 1 auto;
  min-width: 0;
  margin: 0;
  font-size: 13px;
  line-height: 1.4;
  color: var(--ink-2);

  b {
    font-weight: 700;
    color: var(--ink);
  }
`;

/** Faixa do alerta aberto, com o "Tratado" para quem trata a Central (com confirmação). */
export function FaixaDoAlerta({ conversa, podeTratar, aoAtualizarItem }) {
  const { request } = useAuth();
  const toast = useToast();
  const [confirmando, setConfirmando] = useState(false);
  const gatilho = useRef(null);
  const alerta = alertaDaConversa(conversa);
  if (!alerta) return null;

  async function tratar() {
    let resposta;
    try {
      resposta = await marcarAlertaTratado(request, conversa.id);
    } catch {
      // O toast do erro (com o `detalhe`) já saiu pelo `request`; o diálogo fica para tentar de novo.
      return;
    }
    setConfirmando(false);
    const item = resposta?.id ? resposta : { ...conversa, alerta_projetos: { ...conversa.alerta_projetos, aberto: false } };
    aoAtualizarItem(item, { alerta: "tratado" });
    toast.success("Alerta Projetos tratado.");
  }

  const desde = textoDesde(alerta.desde);
  return (
    <Faixa aria-label="Alerta Projetos">
      <IconeProjetos size={16} aria-hidden="true" />
      <TextoDaFaixa>
        <b>Alerta Projetos</b> · {textoDoAlerta(alerta)}
        {desde ? ` ${desde}` : ""}
      </TextoDaFaixa>
      {podeTratar ? (
        <>
          <BotaoCompacto
            ref={gatilho}
            type="button"
            aria-label="Marcar o alerta Projetos como tratado"
            title="O alerta sai da lista e do sino"
            onClick={() => setConfirmando(true)}
          >
            <FiCheck size={15} aria-hidden="true" />
            <span className="rotulo">Tratado</span>
          </BotaoCompacto>
          <ConfirmDialog
            open={confirmando}
            tone="primary"
            titulo="Marcar o alerta como tratado?"
            descricao="O alerta Projetos sai da lista e do sino, e fica registrado que você tratou. Mensagem nova para a equipe de projetos abre outro alerta."
            confirmarLabel="Tratado"
            onConfirmar={tratar}
            onCancelar={() => {
              setConfirmando(false);
              gatilho.current?.focus();
            }}
          />
        </>
      ) : null}
    </Faixa>
  );
}

/** Estado do "Não é de Projetos": qual mensagem está sendo confirmada e o que fazer ao confirmar. */
export function useTirarSelo({ trocarMensagem, aoAtualizarItem }) {
  const { request } = useAuth();
  const toast = useToast();
  const [mensagem, setMensagem] = useState(null);
  const pedir = useCallback((m) => setMensagem(m), []);
  const cancelar = useCallback(() => setMensagem(null), []);
  const confirmar = useCallback(async () => {
    if (!mensagem) return;
    let resposta;
    try {
      resposta = await tirarSeloProjetos(request, mensagem.id);
    } catch {
      // O toast do erro (com o `detalhe`) já saiu pelo `request`; o diálogo fica para tentar de novo.
      return;
    }
    setMensagem(null);
    trocarMensagem(mensagem.id, { projetos: false });
    if (resposta?.conversa?.id) aoAtualizarItem(resposta.conversa, { alerta: "selo" });
    toast.success(resposta?.conversa?.alerta_projetos?.aberto === false ? "Selo tirado e alerta fechado." : "Selo tirado.");
  }, [aoAtualizarItem, mensagem, request, toast, trocarMensagem]);
  return { mensagem, pedir, cancelar, confirmar };
}

export function DialogoTirarSelo({ mensagem, cancelar, confirmar }) {
  return (
    <ConfirmDialog
      open={Boolean(mensagem)}
      tone="primary"
      titulo="Esta mensagem não é de Projetos?"
      descricao="O selo sai desta mensagem e fica registrado que você tirou. Se era a última marcada, o alerta Projetos da conversa fecha. Os selos tirados ajudam a calibrar a IA."
      confirmarLabel="Tirar o selo"
      onConfirmar={confirmar}
      onCancelar={cancelar}
    />
  );
}

const Pendente = styled.section`
  display: grid;
  gap: 10px;
  padding: 12px 14px;
  border-radius: 10px;
  background: var(--warning-row);
  font-size: 13.5px;
  line-height: 1.5;
  color: var(--ink-2);

  > header {
    display: grid;
    gap: 2px;
  }

  h4 {
    display: flex;
    align-items: center;
    gap: 6px;
    margin: 0;
    font-size: 13px;
    font-weight: 700;
    color: var(--ink);

    svg {
      flex: 0 0 auto;
      color: var(--warning-text);
    }
  }

  header p,
  > p {
    margin: 0;
    font-size: 12.5px;
    color: var(--ink-3);
  }

  ul {
    list-style: none;
    display: grid;
    gap: 10px;
    margin: 0;
    padding: 0;
  }

  li {
    display: grid;
    gap: 1px;
    min-width: 0;
  }

  li small {
    font-size: 12px;
    color: var(--ink-3);

    b {
      font-weight: 600;
      color: var(--ink-2);
    }
  }

  li span {
    color: var(--ink);
    overflow-wrap: anywhere;
    text-wrap: pretty;
  }
`;

/** Na prévia: as mensagens que o Jev marcou para Projetos e ainda esperam resposta (as mais novas primeiro). */
export function PendenteParaProjetos({ conversa }) {
  const pendente = pendenteParaProjetos(conversa);
  if (!pendente) return null;
  return (
    <Pendente aria-label="Pendente para Projetos">
      <header>
        <h4>
          <IconeProjetos size={14} aria-hidden="true" />
          Pendente para Projetos
        </h4>
        <p>{pendente.resumo}</p>
      </header>
      {pendente.itens?.length ? (
        <ul>
          {pendente.itens.map((item, i) => (
            <li key={i}>
              <small>
                <b>{item.autor}</b> · {item.quando}
              </small>
              <span>{item.texto}</span>
            </li>
          ))}
        </ul>
      ) : null}
      {pendente.restantes ? (
        <p>
          {pendente.restantes === 1 ? "E mais 1 mensagem anterior" : `E mais ${pendente.restantes} mensagens anteriores`}: abra a
          conversa para ver todas.
        </p>
      ) : null}
    </Pendente>
  );
}
